import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '@/api'
import type { AssetLibraryItem, JobOutput } from '@/api/types'
import { ImageLightbox } from '@/components/ImageLightbox'
import { SmartImage } from '@/components/SmartImage'
import { Icon } from '@/components/icons'
import { Button, Card, CardBody, CardHead, Drawer, Empty, Input } from '@/components/ui'
import { useAssetLibrary } from '@/context/AssetLibraryContext'
import { useJobCenter } from '@/context/JobCenterContext'
import { useToast } from '@/context/ToastContext'
import { groupByTask, type AssetGroup } from '@/lib/assetGroups'
import { rememberFallback } from '@/lib/assetFallback'
import { saveAllAs } from '@/lib/download'
import { FashionStudio } from './FashionStudio'
import {
  buildModelFacets,
  parseModelLook,
  sourceLabelOf,
  type ModelProfile,
} from '@/lib/modelLookParse'

/**
 * AI 模特库 —— 「我生成过哪些人」的资产页。
 *
 * 它替代了顶栏那颗「AI 模特」按钮**原来的落点**（`/professional/products
 * ?feature=product-model`）。用户口径（2026-10-07）：
 *
 *   「点击这个 AI 模特应该去一个新页面，是全部已经生成的 AI 模特的页面，
 *     可以搜索跟筛选的」
 *
 * 为什么这值得一个独立页面，而不是商品图页里的一个页签 —— 两条理由：
 *
 *   ① **输入物相反**（Memory 铁律 6 明文允许独立成页的那条）。
 *      生成页的输入是「模特与场景要求」，产出是图；这一页的输入是
 *      「已经出过的图」，产出是「挑哪个人拿去复用」。一边在造，一边在挑。
 *   ② **寿命不同**。生成页的状态刷新即失（草稿在 `sessionStorage`，
 *      而「模特设定」这组选择刻意不进草稿）；模特库要能关页、明天回来接着挑。
 *
 * ### 一个词的定义：「一个模特」= 一次任务
 *
 * 卡片单位是**任务**，不是单张图。因为 `product-product-model` 的第 1 张是
 * 正面单人照、第 2 张是**同一个人的**三视图（提示词里明文写着「各张之间保持
 * 同一个人」）。拆开摆就是「两张看起来像但不是一个人」——完全错的语义。
 * 归组规则与「我的作品」共用一份实现（`lib/assetGroups.ts`）。
 *
 * ### 数据从哪来
 *
 * `GET /api/asset-library?category=model&origin=generated` —— 复用它而不是
 * 新开一个 `/api/ai-models`：那条接口已经带回了 `appSlug` / `prompt` /
 * `jobId`，模特库要的三样它全有。多开一条 = 多一份会漂移的契约。
 *
 * ### 筛选项从哪来
 *
 * **从已加载的记录里统计**，不写死枚举（`lib/modelLookParse.ts` 的
 * `buildModelFacets`）。写死「非洲 / 拉美」这种没人用过的档，点下去就是空列表。
 */

/** 单页拉多少。资产库接口 `perPage` 上限 500，超了就翻页拉全。 */
const PAGE_SIZE = 500
/** 翻页上限（500 × 6 = 3000 个模特）—— 防后端 `total` 写错时把自己拉死 */
const MAX_PAGES = 6

/** 一个模特 = 一次任务的产出 + 从提示词读回来的设定 */
type ModelEntry = {
  group: AssetGroup
  profile: ModelProfile
  /** 产出它的档（`product-product-model` / `fashion-model-gen`） */
  sourceSlug: string
  cover: AssetLibraryItem
}

type Filters = Partial<Record<string, string>>

export function AiModels() {
  const { addGenerated } = useAssetLibrary()
  const { jobs } = useJobCenter()
  const toast = useToast()

  const [items, setItems] = useState<AssetLibraryItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState<string | null>(null)

  const [rawQuery, setRawQuery] = useState('')
  const [query, setQuery] = useState('')
  const [filters, setFilters] = useState<Filters>({})
  const [source, setSource] = useState('all')

  /** 放大预览：哪个模特、第几张 */
  const [zoom, setZoom] = useState<{ key: string; index: number } | null>(null)
  const [busy, setBusy] = useState(false)

  /**
   * 「生成新模特」抽屉 —— **就地生成，不跳页**。
   *
   * 2026-10-09 用户口径（截图圈着这一页右上角那颗按钮）：
   * 「这个生成模特最好是弹出右边栏目」。此前它是一条 `Link`，点下去整页
   * 换到 `/professional/products?feature=product-model` —— 用户的意图是
   * 「挑不到满意的就再造一个」，跳走等于把「挑」这件事丢在半路，回来还得重新筛。
   */
  const [genOpen, setGenOpen] = useState(false)

  /* ------------------------------ 取数 ------------------------------ */

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const first = await api.listAssetLibrary({
        category: 'model',
        origin: 'generated',
        perPage: PAGE_SIZE,
      })
      let all = first.items
      const pages = Math.min(Math.ceil((first.total || 0) / PAGE_SIZE), MAX_PAGES)
      for (let p = 2; p <= pages; p += 1) {
        const more = await api.listAssetLibrary({
          category: 'model',
          origin: 'generated',
          perPage: PAGE_SIZE,
          page: p,
        })
        if (!more.items.length) break
        all = all.concat(more.items)
      }
      // 登记「图床地址 → 本地副本」兜底：图床图被删/被墙时 SmartImage 自动换本地副本
      all.forEach((it) => rememberFallback(it.thumbUrl || it.url, it.localUrl))
      setItems(all)
      setTotal(first.total || all.length)
      setFailed(null)
    } catch (e) {
      setFailed(e instanceof Error ? e.message : '模特库加载失败')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * 抽屉关闭 = 回去接着挑，先重拉一次。
   *
   * 抽屉里刚提交的模特这时可能已经出图了；不重拉的话它要等到下次进这一页
   * 才出现 —— 用户在库里看不到自己刚造的人，会以为没生成成功。
   */
  const closeGen = useCallback(() => {
    setGenOpen(false)
    void load()
  }, [load])

  /**
   * 生成完成后**自动补进库里**。
   *
   * 抽屉里点完「生成」用户可以立刻关掉它，而图是几十秒后才出的。只在关闭
   * 那一刻拉一次的话，新模特永远要用户手动刷新页面才出现 —— 异步产出得自己
   * 长到界面上（同一条规矩见「异步任务必须占界面位置」）。
   *
   * ⚠️ 判据是「已完成任务的**条数**」，不筛档位：
   *   · 不筛档 —— 模特有两个来源（「AI 模特图」与服饰图的「模特生成」），
   *     而在页面里再抄一份「哪些 slug 出模特」就是第二份清单（归类真源在
   *     `config/assetTaxonomy.ts`）。多拉几次只是几次请求，漏拉一次才是
   *     「刚生成的人不出现」——后者用户会当成没生成成功。
   *   · 不看 `jobs` 本身 —— JobCenter 在轮询，`jobs` 每几秒换一次引用，
   *     直接依赖它等于这一页每隔几秒打一次接口。
   *   首帧只记基线、不触发（上面那次 `load` 已经拉过一遍）。
   */
  const doneCount = useMemo(() => jobs.filter((j) => j.state === 'succeeded').length, [jobs])
  const prevDone = useRef<number | null>(null)
  useEffect(() => {
    const prev = prevDone.current
    prevDone.current = doneCount
    if (prev === null || prev === doneCount) return
    void load()
  }, [doneCount, load])

  // 搜索防抖：每敲一个字就重排整个网格会闪
  useEffect(() => {
    const t = window.setTimeout(() => setQuery(rawQuery.trim().toLowerCase()), 300)
    return () => window.clearTimeout(t)
  }, [rawQuery])

  /* ------------------------------ 派生 ------------------------------ */

  const models = useMemo<ModelEntry[]>(
    () =>
      groupByTask(items).map((g) => {
        const cover = g.items[0]
        return {
          group: g,
          profile: parseModelLook(cover?.prompt),
          sourceSlug: cover?.appSlug ?? '',
          cover,
        }
      }),
    [items],
  )

  /** 按当前筛选之外的全部模特统计 —— 计数是「点了会剩几个」，不是「库里共几个」 */
  const facets = useMemo(() => buildModelFacets(models.map((m) => m.profile)), [models])

  const sourceOptions = useMemo(() => {
    const counter = new Map<string, number>()
    models.forEach((m) => {
      if (!m.sourceSlug) return
      counter.set(m.sourceSlug, (counter.get(m.sourceSlug) ?? 0) + 1)
    })
    return [...counter.entries()]
      .map(([slug, count]) => ({ slug, label: sourceLabelOf(slug), count }))
      .sort((a, b) => b.count - a.count)
  }, [models])

  const visible = useMemo(
    () =>
      models.filter((m) => {
        if (source !== 'all' && m.sourceSlug !== source) return false
        for (const [key, value] of Object.entries(filters)) {
          if (!value) continue
          if (m.profile.tags.find((t) => t.key === key)?.value !== value) return false
        }
        if (!query) return true
        const hay = [
          m.group.title,
          m.profile.note,
          m.profile.tags.map((t) => `${t.label}${t.value}`).join(' '),
          m.cover?.prompt ?? '',
        ]
          .join(' ')
          .toLowerCase()
        return hay.includes(query)
      }),
    [models, filters, source, query],
  )

  const filtered = Object.values(filters).some(Boolean) || source !== 'all' || !!query

  /* ------------------------------ 交互 ------------------------------ */

  /** 切换某个维度的筛选值；再点一次同一个值 = 取消（不设「全部」这个额外档位） */
  const toggleFacet = useCallback((key: string, value: string) => {
    setFilters((f) => ({ ...f, [key]: f[key] === value ? '' : value }))
  }, [])

  const resetAll = useCallback(() => {
    setFilters({})
    setSource('all')
    setRawQuery('')
  }, [])

  /** 把整组（这个人的全部机位）存进参考图素材库 —— 存单张的话「三视图」就丢了 */
  const toAssetLibrary = useCallback(
    (m: ModelEntry) => {
      m.group.items.forEach((it, i) => {
        addGenerated({
          id: `asset-${it.id}-${i}`,
          url: it.url,
          thumbUrl: it.thumbUrl,
          name: `${m.group.title}${m.group.items.length > 1 ? ` #${i + 1}` : ''}`,
          kind: it.kind,
          createdAt: new Date().toISOString(),
          source: 'generated',
          assetCategory: 'model',
        })
      })
      toast.success(`已存入参考图素材库 ${m.group.items.length} 张，可在上传区直接选用`)
    },
    [addGenerated, toast],
  )

  const download = useCallback(
    async (m: ModelEntry) => {
      setBusy(true)
      try {
        await saveAllAs(
          m.group.items
            .filter((it) => it.url)
            .map((it, i) => ({
              url: it.url,
              name: `${m.group.title}${m.group.items.length > 1 ? `_${i + 1}` : ''}`,
            })),
        )
        toast.success(`已开始下载 ${m.group.items.length} 个文件`)
      } finally {
        setBusy(false)
      }
    },
    [toast],
  )

  /* ------------------------------ 渲染 ------------------------------ */

  const zoomEntry = zoom ? models.find((m) => m.group.key === zoom.key) : null
  const zoomItems: JobOutput[] = zoomEntry
    ? zoomEntry.group.items.map((it) => ({ url: it.url, thumbUrl: it.thumbUrl, type: it.kind }))
    : []

  return (
    <div className="page page--wide">
      <Card>
        <CardHead
          title="AI 模特库"
          hint="你生成过的全部真人模特 · 一次生成 = 一个人（正脸 + 三视图是同一个人的两个机位）"
          icon="users"
          right={
            <>
              <Input
                value={rawQuery}
                onChange={(e) => setRawQuery(e.target.value)}
                placeholder="搜索模特 / 设定"
                aria-label="搜索模特"
                data-model-search
                style={{ width: 200 }}
              />
              {/* 「生成新模特」必须留在这一页 —— 顶栏那颗按钮改指这里之后，
                  生成入口就只剩这一条路。少了它，用户会以为模特功能被删了。

                  ⚠️ 2026-10-09 起它是**打开右侧抽屉**，不再跳页（用户口径：
                  「这个生成模特最好是弹出右边栏目」）。抽屉里渲染的仍是
                  `FashionStudio` 本身 —— 生成表单只有一份实现，这里不重写。
                  `data-gen-model` 是自动化锚点（探针要点它）。 */}
              <Button
                variant="outline"
                size="sm"
                icon="sparkles"
                data-gen-model
                onClick={() => setGenOpen(true)}
              >
                生成新模特
              </Button>
            </>
          }
        />
        <CardBody>
          {failed ? (
            <Empty
              icon="alert"
              title="模特库加载失败"
              text={`${failed}。确认后端已启动（默认 127.0.0.1:8000），或改用离线模式。`}
              action={
                <Button variant="outline" size="sm" icon="refresh" onClick={() => void load()}>
                  重试
                </Button>
              }
            />
          ) : !models.length ? (
            <Empty
              icon="users"
              title={loading ? '正在加载…' : '还没有生成过 AI 模特'}
              text={
                loading
                  ? undefined
                  : '在「商品图 → AI 模特图」或「服饰图 → 模特生成」里跑一次，出过的模特会自动收进这里。'
              }
              action={
                loading ? undefined : (
                  <Button
                    variant="primary"
                    size="sm"
                    icon="sparkles"
                    data-gen-model
                    onClick={() => setGenOpen(true)}
                  >
                    去生成第一个模特
                  </Button>
                )
              }
            />
          ) : (
            <>
              {/* 来源档 —— 与模特特征正交（「服饰图的模特」也能「是女性」），可叠加 */}
              {sourceOptions.length > 1 ? (
                <div className="asset-filter" data-source-row>
                  <span className="asset-filter__label">来源</span>
                  <div className="asset-filter__chips">
                    <button
                      type="button"
                      className="chip"
                      data-active={source === 'all'}
                      onClick={() => setSource('all')}
                    >
                      全部 {models.length}
                    </button>
                    {sourceOptions.map((o) => (
                      <button
                        key={o.slug}
                        type="button"
                        className="chip"
                        data-active={source === o.slug}
                        data-source={o.slug}
                        data-count={o.count}
                        onClick={() => setSource(source === o.slug ? 'all' : o.slug)}
                      >
                        {o.label} {o.count}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* 模特特征 —— 选项与计数都从**已加载的记录**统计（见 lib/modelLookParse.ts）。
                  一条都解不出设定时整块不渲染，而不是铺一排点了没结果的空 chip。 */}
              {facets.map((f) => (
                <div className="asset-filter" key={f.key} data-facet-row={f.key}>
                  <span className="asset-filter__label">{f.label}</span>
                  <div className="asset-filter__chips">
                    {f.options.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        className="chip"
                        data-active={filters[f.key] === o.value}
                        data-facet={f.key}
                        data-value={o.value}
                        data-count={o.count}
                        onClick={() => toggleFacet(f.key, o.value)}
                      >
                        {o.value} {o.count}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div className="asset-toolbar">
                <span className="text-xs muted" data-model-count>
                  {loading
                    ? '加载中…'
                    : `共 ${models.length} 个模特${filtered ? ` · 当前筛出 ${visible.length} 个` : ''}${
                        total > items.length ? `（库里共 ${total} 条产出，本次加载 ${items.length} 条）` : ''
                      }`}
                </span>
                {filtered ? (
                  <Button variant="ghost" size="sm" icon="x" onClick={resetAll}>
                    重置筛选
                  </Button>
                ) : null}
              </div>

              {!visible.length ? (
                <Empty icon="search" title="没有匹配的模特" text="换个关键词，或点上面的「重置筛选」。" />
              ) : (
                <div className="modelgrid" data-model-grid>
                  {visible.map((m) => (
                    <ModelCard
                      key={m.group.key}
                      entry={m}
                      busy={busy}
                      onZoom={(i) => setZoom({ key: m.group.key, index: i })}
                      onToAsset={() => toAssetLibrary(m)}
                      onDownload={() => void download(m)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {zoomEntry && zoom !== null ? (
        <ImageLightbox
          title={`${zoomEntry.group.title} · ${zoomEntry.group.items.length} 张`}
          items={zoomItems}
          index={Math.min(zoom.index, zoomItems.length - 1)}
          onIndex={(i) => setZoom({ key: zoomEntry.group.key, index: i })}
          onClose={() => setZoom(null)}
          renderActions={() => (
            <Button size="sm" variant="outline" icon="archive" onClick={() => toAssetLibrary(zoomEntry)}>
              整个模特存素材库
            </Button>
          )}
        />
      ) : null}

      {/*
        「生成新模特」抽屉 —— 就地生成，不进新页面。

        用户的动作是**挑人**：抽屉关掉就回到刚才那张网格，搜索词与筛选一个都没丢。
        这也是本仓库「宽视图 = 弹层不跳页」那条规矩的同一个做法。

        ⚠️ 宽度给到 1240 是为了让生成页保住**双栏**：`.studio` 收成单栏的媒体
        查询看的是**视口**宽度（1220px），不是容器宽度 —— 抽屉窄了它照样按双栏排，
        结果就是左栏被压成一条。所以宽度必须由这里给够（见 global.css
        `.drawer--studio` 那几条）。
        ⚠️ 里层画的就是 `FashionStudio` 本身，只是告诉它「你在抽屉里」：
        类目与档位由这里指死，抽屉里不再出现那排工作流下拉（入口叫「生成新模特」，
        还能切到「白底主图」就是声不对）。
      */}
      <Drawer
        open={genOpen}
        onClose={closeGen}
        title="生成 AI 模特"
        icon="sparkles"
        width={1240}
        className="drawer--studio"
      >
        <FashionStudio embed={{ category: 'products', feature: 'product-model' }} />
      </Drawer>
    </div>
  )
}

/* -------------------------------------------------------------------------- */

/**
 * 一个模特 = 一张封面 + 设定标签 + 两个动作。
 *
 * 封面取**第 1 张产出** —— 也就是提示词里定义的「正面单人照」
 * （见 `modelProfile.modelShotPlan()`：第 1 张永远是正脸单人）。
 * 不按 `createdAt` 或文件名猜，那两个都可能跟张数档位对不上。
 */
function ModelCard({
  entry,
  busy,
  onZoom,
  onToAsset,
  onDownload,
}: {
  entry: ModelEntry
  busy: boolean
  onZoom: (index: number) => void
  onToAsset: () => void
  onDownload: () => void
}) {
  const { group, profile, cover } = entry
  const shot = cover?.thumbUrl || cover?.url || ''

  return (
    <article className="modelcard" data-model-card data-model-src={entry.sourceSlug}>
      {/* 每一格是一个 cell（不是裸按钮）：放大键要挂在封面的**旁边**，
          点图选中、放大走它 —— 与 RefUploader 的候选条同一套规矩。 */}
      <div className="modelcard__cell">
        <button
          type="button"
          className="modelcard__shot"
          title="放大看这个人"
          aria-label="放大看这个人"
          onClick={() => onZoom(0)}
        >
          {shot ? (
            <SmartImage src={shot} alt={group.title} />
          ) : (
            <span className="modelcard__broken">
              <Icon name="alert" size={16} />
            </span>
          )}
          {group.items.length > 1 ? (
            <span className="modelcard__count">{group.items.length} 张</span>
          ) : null}
        </button>
        <button
          type="button"
          className="modelcard__zoom"
          title="放大看这个人"
          aria-label="放大看这个人"
          onClick={() => onZoom(0)}
        >
          <Icon name="expand" size={13} />
        </button>
      </div>

      <div className="modelcard__body">
        <div className="modelcard__name" title={group.title}>
          {group.title}
        </div>
        <div className="modelcard__tags">
          {profile.tags.length ? (
            profile.tags.map((t) => (
              <span className="modelcard__tag" key={t.key} title={t.label}>
                {t.value}
              </span>
            ))
          ) : (
            /* 解不出结构化设定就如实说 —— 编一个「女 / 25 岁」出来会让用户
               按错误的条件去筛，而「筛丢人」比「没有筛选」更坏。 */
            <span className="modelcard__tag modelcard__tag--note" title="这条记录的提示词里没有结构化设定">
              {profile.note || '未记录设定'}
            </span>
          )}
        </div>
        <div className="modelcard__src">{sourceLabelOf(entry.sourceSlug) || '未知来源'}</div>
      </div>

      <div className="modelcard__acts">
        <Button size="sm" variant="ghost" icon="archive" onClick={onToAsset}>
          存素材库
        </Button>
        <Button size="sm" variant="ghost" icon="download" onClick={onDownload} disabled={busy}>
          下载
        </Button>
      </div>
    </article>
  )
}
